import { useState, useCallback } from "react";

interface UseTextHighlightProps {
  contentType: "passage" | "question";
  onSaveHighlight?: (
    contentType: "passage" | "question",
    newContent: string
  ) => void;
}

export const useTextHighlight = ({
  contentType,
  onSaveHighlight,
}: UseTextHighlightProps) => {
  // States
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [colorPickerPosition, setColorPickerPosition] = useState({
    x: 0,
    y: 0,
  });
  const [selectedRange, setSelectedRange] = useState<Range | null>(null);

  // Text selection handler
  const handleTextSelected = useCallback(
    (selectedText: string, rect: DOMRect) => {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        setSelectedRange(selection.getRangeAt(0).cloneRange());
      }

      setColorPickerPosition({
        x: rect.left + window.scrollX,
        y: rect.bottom + window.scrollY + 5,
      });
      setShowColorPicker(true);
    },
    []
  );

  // Close color picker handler
  const handleCloseColorPicker = useCallback(() => {
    setShowColorPicker(false);
    setSelectedRange(null);
  }, []);

  // Color selection handler
  const handleColorSelect = useCallback(
    (color: string) => {
      if (!selectedRange || !onSaveHighlight) return;

      try {
        // Create span for highlighting
        const span = document.createElement("span");
        span.style.backgroundColor = color;
        span.className = "highlight-span";

        // Apply span to Range
        selectedRange.surroundContents(span);

        // Get the updated content from the ContentViewer
        const contentDiv = document.querySelector(
          `[data-content-type='${contentType}']`
        ) as HTMLElement;

        if (contentDiv) {
          const newContent = contentDiv.innerHTML;
          onSaveHighlight(contentType, newContent);
        }
      } catch (error) {
        console.error("Failed to apply highlight:", error);
      }

      setShowColorPicker(false);
      setSelectedRange(null);
      window.getSelection()?.removeAllRanges();
    },
    [selectedRange, onSaveHighlight, contentType]
  );

  return {
    showColorPicker,
    colorPickerPosition,
    handleTextSelected,
    handleCloseColorPicker,
    handleColorSelect,
  };
};
